import { Filter } from './angular-entities';

let app: angular.IModule;
let async: angular.IModule;

const sysAsyncConfig = function (
    $controllerProvider: angular.IControllerProvider,
    $compileProvider: angular.ICompileProvider,
    $filterProvider: angular.IFilterProvider,
    $provide: angular.auto.IProvideService,
    $injector: angular.auto.IInjectorService
) {
    async = {
        controller() {
            $controllerProvider.register.apply(this, arguments as any);
            return this;
        },
        directive() {
            if (!$injector.has(`${arguments[0]}Directive`)) {
                (app as any)._invokeQueue.push(['$compileProvider', 'directive', arguments as any]); // we should add those to _invokeQueue
            }
            $compileProvider.directive.apply(this, arguments as any);
            return this;
        },
        filter() {
            $filterProvider.register.apply(this, arguments as any);
            return this;
        },
        factory() {
            $provide.factory.apply(this, arguments as any);
            return this;
        },
        service() {
            $provide.service.apply(this, arguments as any);
            return this;
        },
        provider() {
            $provide.provider.apply(this, arguments as any);
            return this;
        },
        component() {
            if (!$injector.has(`${arguments[0]}Component`)) {
                (app as any)._invokeQueue.push(['$compileProvider', 'component', arguments as any]); // we should add those to _invokeQueue
            }
            $compileProvider.component.apply(this, arguments as any);
            return this;
        },
        run() {
            app.run.apply(this, arguments as any);
            return this;
        },
        config() {
            app.config.apply(this, arguments as any);
            return this;
        },
        constant(name: string, value?: any) {
            $provide.constant.apply(this, [name, value]);
            return this;
        },
        decorator(name: string, decorator: any) {
            $provide.decorator.apply(this, [name, decorator]);
            return this;
        },
        value(name: string, value?: any) {
            $provide.value.apply(this, [name, value]);
            return this;
        },
        name: app.name,
        requires: app.requires
    };
};
sysAsyncConfig.$inject = ['$controllerProvider', '$compileProvider', '$filterProvider', '$provide', '$injector'];

/**
 * Adapter class for integrating AngularJS modules, directives, services, providers, controllers, and filters
 * using a class-based approach. Provides static and instance methods for registering and linking AngularJS
 * components to a given module.
 *
 * @remarks
 * - The application module must be registered first using {@link AngularAdapter.setApplication}.
 * - Supports dynamic switching between the main application module and an async module.
 * - Provides utility methods for linking directives, services, providers, controllers, and filters.
 *
 * @example
 * ```typescript
 * AngularAdapter.setApplication(appModule);
 * const adapter = new AngularAdapter();
 * adapter.linkDirective(MyDirective);
 * adapter.linkService(MyService);
 * ```
 */
export class AngularAdapter {
    private _app: angular.IModule;
    private _run: boolean = false;

    constructor() {
        if (!app) {
            throw new Error('AngularAdapter.constructor: Angular application should be first registered.');
        }
        this._app = app;
    }

    /**
     * Sets the Angular application module and applies the system asynchronous configuration.
     *
     * @param module - The Angular module to set as the application instance.
     */
    public static setApplication(module: angular.IModule): void {
        app = module;
        app.config(sysAsyncConfig);
    }

    /**
     * Gets the initialized Angular async module.
     *
     * @throws {Error} Throws an error if the async module has not been initialized.
     * @returns {angular.IModule} The initialized async Angular module.
     */
    public get async(): angular.IModule {
        if (!async) {
            throw new Error('Async module has not been initialized.');
        }
        return async;
    }

    /**
     * Gets the Angular module instance.
     * If the `_run` property and `async` are truthy, returns the `async` module; otherwise, returns the `_app` module.
     *
     * @returns {angular.IModule} The selected Angular module instance.
     */
    public get module(): angular.IModule {
        return this._run && async ? async : this._app;
    }

    /**
     * Marks the adapter as running by setting the `_run` flag to `true`.
     * This method can be used to indicate that the adapter's execution has started.
     */
    public run() {
        this._run = true;
    }

    /**
     * Links an Angular directive to the current module using the specified directive class.
     *
     * @param DirectiveClass - The class of the Angular directive to be linked.
     */
    public linkDirective(DirectiveClass: any): void {
        AngularAdapter.linkAngularDirective(this.module, DirectiveClass);
    }

    /**
     * Registers an AngularJS directive with the specified name and factory.
     *
     * @param name - The name of the directive to register.
     * @param factory - The directive factory function, which must be an AngularJS injectable.
     */
    public linkDirectiveTMax(name: string, factory: angular.Injectable<angular.IDirectiveFactory>): void {
        this.module.directive(name, factory);
    }

    /**
     * Links an Angular service to the current module using the provided service class.
     *
     * @param ServiceClass - The constructor of the Angular service to be linked.
     */
    public linkService(ServiceClass: any): void {
        AngularAdapter.linkAngularService(this.module, ServiceClass);
    }

    /**
     * Links an Angular provider to the current module using the specified provider class.
     *
     * @param ProviderClass - The class of the provider to be linked to the Angular module.
     */
    public linkProvider(ProviderClass: any): void {
        AngularAdapter.linkAngularProvider(this.module, ProviderClass);
    }

    /**
     * Links an Angular filter to the current module using the provided filter class.
     *
     * @param FilterClass - The filter class to be linked to the Angular module.
     */
    public linkFilter(FilterClass: any): void {
        AngularAdapter.linkAngularFilter(this.module, FilterClass);
    }

    /**
     * Links an Angular controller to the current module using the specified controller class.
     *
     * @param ControllerClass - The class of the controller to be linked to the Angular module.
     */
    public linkController(ControllerClass: any): void {
        AngularAdapter.linkAngularController(this.module, ControllerClass);
    }

    /**
     * Links an AngularJS directive class to an AngularJS module by registering it as a directive.
     *
     * This method constructs the directive configuration object from static properties and instance methods
     * of the provided `DirectiveClass`, and registers it on the specified AngularJS module.
     *
     * The configuration supports standard AngularJS directive options such as `templateUrl`, `replace`, `transclude`,
     * `require`, `scope`, `controller`, `controllerAs`, `priority`, `terminal`, `restrict`, and `bindToController`.
     * It also supports custom demo-related properties (`$demo`, `$demoScope`, `$demoDescription`, `$tags`) for documentation purposes.
     *
     * The directive's `compile` or `link` methods are bound to the directive instance if present.
     * If a `template` method is defined and `noTemplate` is not set, it will be used for the directive's template.
     *
     * @param module - The AngularJS module to register the directive with.
     * @param DirectiveClass - The directive class containing static configuration and instance methods.
     */
    public static linkAngularDirective(module: angular.IModule, DirectiveClass: any): void {
        const directive = function (...args: any[]) {
            const directive = new DirectiveClass(...args);
            const config: any = {
                templateUrl: DirectiveClass.$templateUrl,
                replace: DirectiveClass.$replace,
                transclude: DirectiveClass.$transclude,
                require: DirectiveClass.$require
            };
            if ((typeof directive.canCompile === 'undefined' && directive.compile) || (directive.canCompile && directive.canCompile())) {
                config.compile = directive.compile.bind(directive);
            } else if (directive.link) {
                config.link = directive.link.bind(directive);
            }
            if (DirectiveClass.$scope) {
                config.scope = DirectiveClass.$scope;
            }
            if (DirectiveClass.$controller) {
                config.controller = DirectiveClass.$controller;
            }
            if (DirectiveClass.$controllerAs) {
                config.controllerAs = DirectiveClass.$controllerAs;
            }
            if (DirectiveClass.$priority) {
                config.priority = DirectiveClass.$priority;
            }
            if (DirectiveClass.$terminal) {
                config.terminal = DirectiveClass.$terminal;
            }
            if (DirectiveClass.$restrict) {
                config.restrict = DirectiveClass.$restrict;
            }
            if (DirectiveClass.$bindToController) {
                config.controllerAs = DirectiveClass.$controllerAs || '$ctrl';
                config.bindToController = DirectiveClass.$bindToController;
            }
            if (!directive.noTemplate && directive.template) {
                config.template = directive.template.bind(directive);
            }

            // demo section
            if (DirectiveClass.$demo) {
                config.demo = DirectiveClass.$demo;
            }
            if (DirectiveClass.$demoScope) {
                config.demoScope = DirectiveClass.$demoScope;
            }
            if (DirectiveClass.$demoDescription) {
                config.demoDescription = DirectiveClass.$demoDescription;
            }
            if (DirectiveClass.$tags) {
                config.tags = DirectiveClass.$tags;
            }
            return config;
        };
        directive.$inject = DirectiveClass.$inject;
        module.directive(DirectiveClass.$name, directive);
    }

    /**
     * Links an Angular service class to the specified Angular module.
     *
     * Registers the provided `ServiceClass` as a service on the given `module` using
     * the service name defined in `ServiceClass.$name`.
     *
     * @param module - The Angular module to register the service with.
     * @param ServiceClass - The service class to be registered. Must have a static `$name` property.
     */
    public static linkAngularService(module: angular.IModule, ServiceClass: any) {
        module.service(ServiceClass.$name, ServiceClass);
    }

    /**
     * Links an AngularJS provider class to a specified module.
     *
     * This method sets up the `$get` dependency injection for the provider class
     * and registers the provider with the AngularJS module using the provider's `$name`.
     *
     * @param module - The AngularJS module to register the provider with.
     * @param ProviderClass - The provider class to be linked and registered.
     *
     * @remarks
     * - Assumes `ProviderClass` has static properties `$name` and `$getInject`.
     * - Assumes `ProviderClass.prototype.$get` exists and is the provider factory function.
     */
    public static linkAngularProvider(module: angular.IModule, ProviderClass: any) {
        ProviderClass.prototype.$get.$inject = ProviderClass.$getInject;
        module.provider(ProviderClass.$name, ProviderClass);
    }

    /**
     * Registers an AngularJS controller with the specified module using the controller's `$name` property.
     *
     * @param module - The AngularJS module to register the controller with.
     * @param ControllerClass - The controller class to be registered. Must have a static `$name` property.
     */
    public static linkAngularController(module: angular.IModule, ControllerClass: any) {
        module.controller(ControllerClass.$name, ControllerClass);
    }

    public static linkAngularFilter(module: angular.IModule, FilterClass: typeof Filter) {
        const filter: any[] = FilterClass.$inject || [];
        filter.push(FilterClass.$get);
        module.filter(FilterClass.$name, filter);
    }
}
